import { useCallback, memo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useQueryClient } from '@tanstack/react-query';
// mantine
import { Button, Container, TextInput, Transition } from '@mantine/core';
// components
import { Search } from 'lucide-react';
import type { TAttributeData } from '@newo/api/gateway';
import PublishAgentButton from '@/pages/Settings/components/PublishAgentButton';
// icons
// utils
import { cn } from '@/utils';
import useSettingsStore from '@/pages/Settings/Settings.store';
import { useCustomerUpdateAttributesMutation } from '@/services/customer/customer/customer.queries';
import { useChangeConnectorSettingsMutation } from '@/services/integration/integration.queries';
// assets
import styles from '@/pages/Settings/components/Toolbar/Toolbar.module.css';

interface ToolbarProps {
    searchTerm: string;
    onSearchChange: (searchTerm: string) => void;
}

const Toolbar = memo(({ searchTerm, onSearchChange }: ToolbarProps) => {
    const queryClient = useQueryClient();
    const {
        attributesValues,
        attributesErrors,
        chatValues,
        chatErrors,
        voiceValues,
        voiceErrors,
        reset
    } = useSettingsStore(
        useShallow((state) => ({
            attributesValues: state.attributesValues,
            attributesErrors: state.attributesErrors,
            chatValues: state.chatValues,
            chatErrors: state.chatErrors,
            voiceValues: state.voiceValues,
            voiceErrors: state.voiceErrors,
            reset: state.reset
        }))
    );

    const { mutateAsync: updateAttributes } =
        useCustomerUpdateAttributesMutation();
    const { mutateAsync: updateConnectorSettings } =
        useChangeConnectorSettingsMutation();

    const hasPendingChanges =
        Object.keys(attributesValues).length > 0 ||
        Object.keys(chatValues).length > 0 ||
        Object.keys(voiceValues).length > 0;

    const hasErrors = [
        ...Object.values(attributesErrors),
        ...Object.values(chatErrors),
        ...Object.values(voiceErrors)
    ].some((error) => !!error);

    const handleCancel = useCallback(() => {
        reset();
    }, [reset]);

    const handleSaveAll = useCallback(async () => {
        if (hasErrors) {
            console.error('Cannot save: There are validation errors');
            return;
        }

        const attributesUpdates = updateAttributes({
            data: Object.values(attributesValues).filter(
                (attribute): attribute is TAttributeData =>
                    attribute !== undefined
            )
        });

        const chatUpdates = Object.entries(chatValues).map(
            async ([connectorId, data = []]) =>
                updateConnectorSettings({ connectorId, data })
        );

        const voiceUpdates = Object.entries(voiceValues).map(
            async ([connectorId, data = []]) =>
                updateConnectorSettings({ connectorId, data })
        );

        try {
            await Promise.all([
                attributesUpdates,
                ...chatUpdates,
                ...voiceUpdates
            ]);

            await Promise.allSettled([
                queryClient.invalidateQueries({
                    queryKey: ['integration/by-idn/connectors']
                }),
                queryClient.invalidateQueries({
                    queryKey: ['bff/customer/attributes']
                })
            ]);
            reset();
            await queryClient.invalidateQueries({
                queryKey: ['bff/customer/attributes']
            });
            reset();
        } catch (error) {
            console.error('Failed to save changes:', error);
        }
    }, [
        attributesValues,
        chatValues,
        voiceValues,
        updateAttributes,
        updateConnectorSettings,
        reset,
        hasErrors,
        queryClient
    ]);

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onSearchChange(event.currentTarget.value);
        },
        [onSearchChange]
    );

    return (
        <div
            className={cn(
                'fixed left-0 w-full bg-white z-10 py-4 shadow-xs',
                styles.toolbar
            )}
        >
            <Container className="flex gap-4 justify-between" size="xl">
                <TextInput
                    className="w-full max-w-md"
                    defaultValue={searchTerm}
                    leftSection={<Search />}
                    placeholder="Search by idn, title, group"
                    size="xs"
                    onChange={handleSearchChange}
                />
                <div className="flex gap-2 relative">
                    <Transition
                        duration={200}
                        mounted={hasPendingChanges}
                        transition="fade"
                    >
                        {(css) => (
                            <div
                                style={{
                                    ...css,
                                    position: 'absolute',
                                    right: 0
                                }}
                            >
                                <div className="flex gap-2">
                                    <Button
                                        disabled={hasErrors}
                                        size="xs"
                                        variant="filled"
                                        onClick={handleSaveAll}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="default"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Transition>
                    <Transition
                        duration={200}
                        mounted={!hasPendingChanges}
                        transition="fade"
                    >
                        {(css) => (
                            <div
                                style={{
                                    ...css,
                                    position: 'absolute',
                                    right: 0
                                }}
                            >
                                <PublishAgentButton />
                            </div>
                        )}
                    </Transition>
                </div>
            </Container>
        </div>
    );
});

Toolbar.displayName = 'Toolbar';

export default Toolbar;
